import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styles from './Distribution.module.scss';

import PageWrapper from '../../components/PageWrapper/PageWrapper';
import ProgressButtons from '../../components/Progress/ProgressButtons/ProgressButtons';
import ProgressBar from '../../components/Progress/ProgressBar/ProgressBar';
import {
  DISTRIBUTION_METHOD_STEP,
  PROPORTIONATE_DISTRIBUTION_METHOD_STEP,
  TOLL_FREE_NUMBER,
  CUSTOM_DISTRIBUTION_METHOD_STEP,
  REINVEST_STEP,
  FREQUENCY_STEP,
  BANK_STEP,
  ADDRESS_STEP,
  DESTINATION_STEP,
  REINVEST_TO_NEW,
} from '../../settings/settings';
import RMLConnection from '../../components/RMLConnection/RMLConnection';
import {
  getRMDfunds,
  getBalanceAsOfDate,
} from '../../redux/modules/UserFunds/userFunds.selectors';
import DestinationOption from '../../components/DestinationOption/DestinationOption';
import HelpBox from '../../components/HelpBox/HelpBox';
import FundDisplayText from '../ProportionateDistributionMethod/FundDisplayText';
import {
  getSelectedDestination,
  getSelectedDistribution,
} from '../../redux/modules/shoppingCart/shoppingCart.selectors';
import { getHouseholdSegmentInformation } from '../../redux/modules/PersonalInformation/personalInformation.selectors';
import { moduleActions } from '../../redux/modules/index';
import checkmark from '../../assets/images/checkmark.svg';
import lock from '../../assets/images/lock.svg';
import proportionallyImage from '../../assets/images/proportionally-image.svg';
import customize from '../../assets/images/customise.svg';
import alertIcon from '../../assets/images/alert.svg';
import config from '../../config/config';
import { getRMDAgreements } from '../../redux/modules/RMD/RMDSetting.selectors';

const Distribution = (props) => {
  const dispatch = useDispatch();

  const {
    ROLLOVER_IRA: {
      unlockedFunds: rolloverUnlockedFunds,
      lockedFunds: rolloverLockedFunds,
    },
    TRADITIONAL_IRA: {
      unlockedFunds: traditionalUnlockedFunds,
      lockedFunds: traditionalLockedFunds,
    },
    allFunds: fundList,
  } = useSelector(getRMDfunds);
  const rmdAgreements = useSelector(getRMDAgreements);
  const allUnlockedFundsTemp = traditionalUnlockedFunds.concat(
    rolloverUnlockedFunds
  );
  const allLockedFundsTemp = traditionalLockedFunds.concat(rolloverLockedFunds);
  const allFundsTemp = allUnlockedFundsTemp.concat(allLockedFundsTemp);
  const selectedDestination = useSelector(getSelectedDestination);
  const selectedDistribution = useSelector(getSelectedDistribution);
  const balanceAsOfDate = useSelector(getBalanceAsOfDate);
  /* Getting household entity segment information */
  const enableDistributionMethod = useSelector(getHouseholdSegmentInformation);
  const [selected, setSelected] = useState(selectedDistribution);
  const showFrequencyPage =
    selectedDestination === BANK_STEP || selectedDestination === ADDRESS_STEP;

  const handleOnSelected = (selected) => {
    setSelected(selected);
    setDisable(false);
  };

  const [traditionalLocked, setTraditionalLocked] = useState(false);
  const [rolloverLocked, setRolloverLocked] = useState(false);

  const isCardLocked =
    rolloverLockedFunds.length > 0 || traditionalLockedFunds.length > 0;

  /* Only show custom distribution method when the user is in Enhanced Personal Services */
  const hideCustomDistributionMethod =
    (!enableDistributionMethod && !isCardLocked) ||
    (allUnlockedFundsTemp.length === 1 && fundList.length === 1);

  if (
    hideCustomDistributionMethod &&
    (selectedDistribution === 'none' || selectedDistribution === null)
  ) {
    if (rolloverUnlockedFunds && rolloverUnlockedFunds.length > 0) {
      dispatch(
        moduleActions.searchExchangeToExistingRollover(rolloverUnlockedFunds[0])
      );
    }
    if (traditionalUnlockedFunds && traditionalUnlockedFunds.length > 0) {
      dispatch(
        moduleActions.searchExchangeToExistingTraditional(
          traditionalUnlockedFunds[0]
        )
      );
    }
    dispatch(
      moduleActions.updateSelectedDistributionType(
        PROPORTIONATE_DISTRIBUTION_METHOD_STEP
      )
    );
    dispatch(
      moduleActions.updateSelectedDistributionFunds({
        traditionalFunds: traditionalUnlockedFunds,
        rolloverFunds: rolloverUnlockedFunds,
      })
    );
  }

  const [continueDisable, setDisable] = useState(
    hideCustomDistributionMethod
      ? false
      : (allFundsTemp.length !== 1 &&
          (selected === 'none' || selected === null)) ||
          (isCardLocked && (selected === 'none' || selected === null))
  );
  return (
    <div className="rmd-distribution-wrapper">
      <div className="sticky-header">
        <div className="heading">Auto-RMD Setup</div>
        <ProgressButtons
          edit={props.edit}
          currentStep={DISTRIBUTION_METHOD_STEP}
          currentSelection={selectedDistribution}
          payload={
            allFundsTemp.length > 1 && selected !== 'none' ? selected : false
          }
          previousStep={
            selectedDestination === REINVEST_STEP ||
            selectedDestination === REINVEST_TO_NEW
              ? DESTINATION_STEP
              : selectedDestination
          }
          nextStep={
            hideCustomDistributionMethod && showFrequencyPage
              ? FREQUENCY_STEP
              : hideCustomDistributionMethod && !showFrequencyPage
              ? REINVEST_STEP
              : selected
          }
          {...props}
          showBackButton={true}
          backDisabled={false}
          disabled={continueDisable}
        >
          <ProgressBar completedSteps={[0]} activeIndex={1} />
        </ProgressButtons>
      </div>
      <PageWrapper step={DISTRIBUTION_METHOD_STEP}>
        <div className="scroll-wrapper">
          <div className="reinvest-title">
            {!hideCustomDistributionMethod ? (
              <span>
                How would you like to withdraw your money from your{' '}
                <span className="orphan-line"> eligible IRAs?</span>
              </span>
            ) : hideCustomDistributionMethod && allFundsTemp.length > 1 ? (
              <span>
                These are the IRAs we will <br /> proportionally withdraw from:
              </span>
            ) : allFundsTemp.length === 1 ? (
              'Your RMD will be withdrawn from:'
            ) : (
              ''
            )}
          </div>
          <div className={styles['option-container']}>
            {hideCustomDistributionMethod && allFundsTemp.length > 0 ? (
              <>
                {allFundsTemp.length === 1 && (
                  <div className={styles['sub-title']}>
                    {' '}
                    {allFundsTemp[0].agreementType === 'TRADITIONAL_IRA'
                      ? 'Traditional IRA'
                      : 'Rollover IRA'}
                  </div>
                )}
                {allFundsTemp.map((fund, key) => (
                  <DestinationOption
                    isSelected={true}
                    key={key}
                    optionText={
                      <FundDisplayText
                        retirement={true}
                        balanceAsOfDate={balanceAsOfDate}
                        fund={fund}
                        hasRmdEligibleFunds={allFundsTemp.length > 1}
                      />
                    }
                    selectable
                    value={1}
                    blockContainer={true}
                  />
                ))}
              </>
            ) : (
              <>
                {allFundsTemp.length >= 0 && (
                  <>
                    <div className={styles['cards-div']}>
                      <div
                        className={`${styles['card']} ${
                          isCardLocked && styles['disbaled-card']
                        } ${
                          selected === PROPORTIONATE_DISTRIBUTION_METHOD_STEP &&
                          styles['selected-card']
                        }`}
                        onClick={() => {
                          if (!isCardLocked)
                            handleOnSelected(
                              PROPORTIONATE_DISTRIBUTION_METHOD_STEP
                            );
                        }}
                      >
                        {isCardLocked ? (
                          <img
                            className={styles['lock-img']}
                            alt="lock"
                            src={lock}
                          />
                        ) : (
                          <img
                            src={checkmark}
                            alt=""
                            className={`${
                              selected ===
                              PROPORTIONATE_DISTRIBUTION_METHOD_STEP
                                ? styles['check-img']
                                : styles['hidden']
                            }`}
                          />
                        )}

                        <div className={styles['cardImage']}>
                          <img
                            alt="proportionality-investment"
                            src={proportionallyImage}
                          />
                        </div>
                        <div className={styles['caredescription-div']}>
                          <div className={styles.cardTitle}>
                            Proportionally{' '}
                            <br className={styles['line-break']} />
                            by Investment
                          </div>
                          <div className={styles.cardSeperator}></div>
                          <div className={styles.cardDescription}>
                            Your RMD will be taken proportionally among your
                            investments. Your largest investment will contribute
                            more to your RMD than your smallest investment.
                          </div>
                        </div>
                      </div>
                      {enableDistributionMethod && fundList.length > 1 && (
                        <div
                          className={`${styles['card']} ${
                            selected === CUSTOM_DISTRIBUTION_METHOD_STEP &&
                            styles['selected-card']
                          }`}
                          id="customDistribution"
                          onClick={() =>
                            handleOnSelected(CUSTOM_DISTRIBUTION_METHOD_STEP)
                          }
                        >
                          <img
                            src={checkmark}
                            alt=""
                            className={`${
                              selected === CUSTOM_DISTRIBUTION_METHOD_STEP
                                ? styles['check-img']
                                : styles['hidden']
                            }`}
                          />
                          <div className={styles['cardImage']}>
                            <img alt="distribution" src={customize} />
                          </div>
                          <div className={styles['caredescription-div']}>
                            <div className={styles.cardTitle}>
                              Customize Your Distribution
                            </div>
                            <div className={styles.cardSeperator}></div>
                            <div className={styles.cardDescription}>
                              Select how much of each investment should make up
                              your RMD.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {(isCardLocked || allFundsTemp.length === 0) && (
                      <div>
                        <div className={styles['warning-banner']}>
                          <img
                            className={styles['lockedcard-img']}
                            src={alertIcon}
                            alt="alert icon"
                          />
                          <h3 className={styles['lockedcard-title']}>
                            Ineligible Accounts
                          </h3>
                          <div className={styles['lockedcard-desc']}>
                            A restriction has been placed on your account(s).{' '}
                            <a
                              href={`${config.url}/personal-investing/help/contact/index.html`}
                              rel="noopener noreferrer"
                              target="_blank"
                              className={styles['lockedcard-link']}
                            >
                              Contact us
                            </a>{' '}
                            for more information
                            {enableDistributionMethod &&
                              fundList.length > 1 && (
                                <span>
                                  {' '}
                                  or customize your distribution using your
                                  available accounts.{' '}
                                </span>
                              )}
                            {!(
                              enableDistributionMethod && fundList.length > 1
                            ) && <span>. </span>}
                          </div>
                        </div>
                        {traditionalLockedFunds.length > 0 && (
                          <div>
                            <h1 className={styles['locked-sub-title']}>
                              Traditional IRA
                            </h1>
                            {!traditionalLocked && (
                              <div>
                                {traditionalLockedFunds
                                  .slice(0, 2)
                                  .map((fund, index) => (
                                    <DestinationOption
                                      key={index}
                                      addressLock={true}
                                      blockContainer={true}
                                      disabled={true}
                                      isLockedFund={true}
                                      optionText={
                                        <FundDisplayText
                                          balanceAsOfDate={balanceAsOfDate}
                                          fund={fund}
                                          lock={true}
                                          hasRmdEligibleFunds={
                                            allFundsTemp.length > 1
                                          }
                                        />
                                      }
                                      selectable={false}
                                      value={1}
                                      balanceAsOfDate={balanceAsOfDate}
                                    />
                                  ))}
                              </div>
                            )}

                            {traditionalLockedFunds.map((fund, index) => (
                              <div key={index}>
                                {traditionalLocked && (
                                  <DestinationOption
                                    key={index}
                                    addressLock={true}
                                    blockContainer={true}
                                    disabled={true}
                                    isLockedFund={true}
                                    optionText={
                                      <FundDisplayText
                                        balanceAsOfDate={balanceAsOfDate}
                                        fund={fund}
                                        lock={true}
                                        hasRmdEligibleFunds={
                                          allFundsTemp.length > 1
                                        }
                                      />
                                    }
                                    selectable={false}
                                    value={1}
                                    balanceAsOfDate={balanceAsOfDate}
                                  />
                                )}
                              </div>
                            ))}
                            {!traditionalLocked &&
                              traditionalLockedFunds.length > 2 && (
                                <button
                                  className={styles['show-more-funds']}
                                  onClick={() =>
                                    setTraditionalLocked(!traditionalLocked)
                                  }
                                >
                                  Show all Traditional Funds
                                </button>
                              )}
                          </div>
                        )}
                        {rolloverLockedFunds.length > 0 && (
                          <>
                            <h1 className={styles['locked-sub-title']}>
                              Rollover IRA
                            </h1>
                            {!rolloverLocked && (
                              <div>
                                {rolloverLockedFunds
                                  .slice(0, 2)
                                  .map((fund, index) => (
                                    <DestinationOption
                                      key={index}
                                      addressLock={true}
                                      blockContainer={true}
                                      disabled={true}
                                      isLockedFund={true}
                                      optionText={
                                        <FundDisplayText
                                          balanceAsOfDate={balanceAsOfDate}
                                          fund={fund}
                                          lock={true}
                                          hasRmdEligibleFunds={
                                            allFundsTemp.length > 1
                                          }
                                        />
                                      }
                                      selectable={false}
                                      value={1}
                                      balanceAsOfDate={balanceAsOfDate}
                                    />
                                  ))}
                              </div>
                            )}
                            {rolloverLockedFunds.map((fund, index) => (
                              <div key={index}>
                                {rolloverLocked && (
                                  <DestinationOption
                                    key={index}
                                    addressLock={true}
                                    blockContainer={true}
                                    disabled={true}
                                    isLockedFund={true}
                                    optionText={
                                      <FundDisplayText
                                        balanceAsOfDate={balanceAsOfDate}
                                        fund={fund}
                                        lock={true}
                                        hasRmdEligibleFunds={
                                          allFundsTemp.length > 1
                                        }
                                      />
                                    }
                                    selectable={false}
                                    value={1}
                                    balanceAsOfDate={balanceAsOfDate}
                                  />
                                )}
                              </div>
                            ))}
                            {!rolloverLocked &&
                              rolloverLockedFunds.length > 2 && (
                                <button
                                  className={styles['show-more-funds']}
                                  onClick={() =>
                                    setRolloverLocked(!rolloverLocked)
                                  }
                                >
                                  Show all Rollover Funds
                                </button>
                              )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {<HelpBox step={DISTRIBUTION_METHOD_STEP} />}
            <RMLConnection phoneNumber={TOLL_FREE_NUMBER} />
          </div>
        </div>
      </PageWrapper>
    </div>
  );
};

export default Distribution;
